// src/store/slices/secondaryLineSlice.ts
/**
 * Secondary (double) number line configuration with localStorage persistence
 * Implements Section 5.3 of the specification
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SecondaryLineState, Fraction } from '../../types/redux';

const SECONDARY_LINE_STORAGE_KEY = 'nl:secondaryLine';

// Default secondary line state
const defaultSecondaryLineState: SecondaryLineState = {
  enabled: false, // Disabled by default
  scaleFactor: { n: 2, d: 1 }, // 2x by default
  numberType: 'fraction',
  fractionDisplay: {
    format: 'improper',
    simplify: true,
  },
  locked: true, // proportional by default
  offset: 0, // T-24: Added offset
  visible: true, // T-24: Added visibility
  hideAllLabels: false, // Show all labels by default
};

/**
 * Load secondary line state from localStorage
 */
function loadSecondaryLineFromStorage(): SecondaryLineState {
  try {
    const stored = localStorage.getItem(SECONDARY_LINE_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle new property additions
      let secondaryLine = { ...defaultSecondaryLineState, ...parsed };
      let updated = false;

      // Migrate legacy default scale factor of 1/2 to new 2/1
      if (secondaryLine.scaleFactor?.n === 1 && secondaryLine.scaleFactor?.d === 2) {
        secondaryLine = {
          ...secondaryLine,
          scaleFactor: defaultSecondaryLineState.scaleFactor,
        };
        updated = true;
      }

      if (updated) {
        saveSecondaryLineToStorage(secondaryLine);
      }
      console.log('📐 Loaded secondary line state:', secondaryLine);
      return secondaryLine;
    }
  } catch (error) {
    console.warn('Failed to load secondary line from storage:', error);
  }
  
  // If no stored state, try to get default scale factor from preferences
  try {
    const preferencesStored = localStorage.getItem('nl:preferences');
    if (preferencesStored) {
      const preferences = JSON.parse(preferencesStored);
      if (preferences.defaultScaleFactor) {
        const stateWithPrefs = { 
          ...defaultSecondaryLineState, 
          scaleFactor: preferences.defaultScaleFactor 
        };
        console.log('📐 Using preferences default scale factor:', stateWithPrefs);
        return stateWithPrefs;
      }
    }
  } catch (error) {
    console.warn('Failed to load preferences for default scale factor:', error);
  }
  
  console.log('📐 Using default secondary line state:', defaultSecondaryLineState);
  return defaultSecondaryLineState;
}

/**
 * Save secondary line state to localStorage
 */
function saveSecondaryLineToStorage(secondaryLine: SecondaryLineState): void {
  try {
    localStorage.setItem(SECONDARY_LINE_STORAGE_KEY, JSON.stringify(secondaryLine));
  } catch (error) {
    console.warn('Failed to save secondary line to storage:', error);
  }
}

const initialState: SecondaryLineState = loadSecondaryLineFromStorage();

export const secondaryLineSlice = createSlice({
  name: 'secondaryLine',
  initialState,
  reducers: {
    /**
     * Enable or disable the secondary line
     */
    setEnabled: (state, action: PayloadAction<boolean>) => {
      state.enabled = action.payload;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Toggle secondary line on/off
     */
    toggleSecondaryLine: (state) => {
      console.log('🔄 toggleSecondaryLine reducer called - current state:', state.enabled);
      state.enabled = !state.enabled;
      console.log('🔄 toggleSecondaryLine reducer completed - new state:', state.enabled);
      saveSecondaryLineToStorage(state);
    },

    /**
     * Set the scale factor (secondary = primary * scaleFactor)
     */
    setScaleFactor: (state, action: PayloadAction<Fraction>) => {
      state.scaleFactor = action.payload;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Set number type for secondary line
     */
    setSecondaryNumberType: (state, action: PayloadAction<'integer' | 'decimal' | 'fraction' | 'percent'>) => {
      state.numberType = action.payload;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Toggle fraction format for secondary line
     */
    toggleSecondaryFractionFormat: (state) => {
      state.fractionDisplay.format = 
        state.fractionDisplay.format === 'mixed' ? 'improper' : 'mixed';
      saveSecondaryLineToStorage(state);
    },

    /**
     * Toggle fraction simplification for secondary line
     */
    toggleSecondaryFractionSimplify: (state) => {
      state.fractionDisplay.simplify = !state.fractionDisplay.simplify;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Set whether secondary line is locked to primary proportions
     */
    setLocked: (state, action: PayloadAction<boolean>) => {
      state.locked = action.payload;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Toggle hide all labels for secondary line
     */
    toggleHideAllLabels: (state) => {
      state.hideAllLabels = !state.hideAllLabels;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Set hide all labels for secondary line
     */
    setHideAllLabels: (state, action: PayloadAction<boolean>) => {
      state.hideAllLabels = action.payload;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Reset secondary line to defaults
     */
    resetSecondaryLine: (state) => {
      Object.assign(state, defaultSecondaryLineState);
      saveSecondaryLineToStorage(state);
    },

    /**
     * Load secondary line state from shared scene
     */
    loadSecondaryLineState: (state, action: PayloadAction<SecondaryLineState>) => {
      Object.assign(state, action.payload);
      saveSecondaryLineToStorage(state);
    },
  },
});

export const {
  setEnabled,
  toggleSecondaryLine,
  setScaleFactor,
  setSecondaryNumberType,
  toggleSecondaryFractionFormat,
  toggleSecondaryFractionSimplify,
  setLocked,
  toggleHideAllLabels,
  setHideAllLabels,
  resetSecondaryLine,
  loadSecondaryLineState,
} = secondaryLineSlice.actions;

export default secondaryLineSlice.reducer;